import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from "rxjs";
import { AppInterface } from 'src/app/core/interfaces/app.interface';
import { FolderInterface } from 'src/app/core/interfaces/folder.interface';
import { DragElementEnum } from '../enums/drag-element.enum';
import { CURSOR } from 'src/app/core/interfaces/cursor.interface';
import { CursorService } from 'src/app/core/services/cursor.service';

@Injectable({
  providedIn: 'root',
})
export class DragDropService {
  private readonly _currentDragElement = signal<unknown | null>(null);
  public readonly currentDragElement = this._currentDragElement.asReadonly();
  private currentDragType: DragElementEnum = DragElementEnum.NOTHING;
  private dropContainer: DragElementEnum = DragElementEnum.NOTHING;
  private lastDropContainer: DragElementEnum = DragElementEnum.NOTHING;

  private readonly _dropTargetFolder = signal<FolderInterface | null>(null);
  public readonly dropTargetFolder = this._dropTargetFolder.asReadonly();

  private widgetIsDragging = new BehaviorSubject<boolean>(false);
  widgetIsDragging$ = this.widgetIsDragging.asObservable();

  private cursorService = inject(CursorService);

  // Live-Preview: Event for drag stop
  private dragStopped = new BehaviorSubject<void>(undefined);
  // Performance optimization: Debounced drop container updates
  private dropContainerSubject = new BehaviorSubject<DragElementEnum>(DragElementEnum.NOTHING);

  constructor() {
  }

  setWidgetDragging(dragState: boolean): void {
    this.widgetIsDragging.next(dragState);
  }

  public getCurrentDragType() {
    return this.currentDragType;
  }

  public startDragging(_element: any, _dragType: DragElementEnum): void {
    this.cursorService.setCursor(CURSOR.GRABBING);
    if (_dragType === DragElementEnum.WIDGET) {
      this.setWidgetDragging(true);
    }

    this.setDropContainer(_dragType);
    this._currentDragElement.set(_element)
    this.currentDragType = _dragType;
  }

  public stopDragging() {
    this.cursorService.setToDefault();
    
    if (this.widgetIsDragging.getValue()) {
      this.setWidgetDragging(false);
    }
    // Live-Preview: message to all components, that dragging was stopped
    this.dragStopped.next();

    this._currentDragElement.set(null);
    this.currentDragType = DragElementEnum.NOTHING;
    this.setDropContainer(DragElementEnum.NOTHING);
  }

  public setDropContainer(_dropContainer: DragElementEnum) {
    this.lastDropContainer = this.dropContainer;
    this.dropContainer = _dropContainer;
    this.dropContainerSubject.next(_dropContainer);
  }

  public getDropContainer(): DragElementEnum {
    return this.dropContainer;
  }

  public getLastDropContainer(): DragElementEnum {
    return this.lastDropContainer;
  }

  updateDropContainerIfDraggingSomething(_dragElementEnum: DragElementEnum) {
    if (this.getCurrentDragType() === DragElementEnum.NOTHING) {
      return;
    }
    this.setDropContainer(_dragElementEnum);
  }

  public isDropWidgetIntoSameCurrentFolder(folder: FolderInterface): boolean {
    //For disable drop preview
    const currentDragApp = this.currentDragElement() as AppInterface
    return this.currentDragType === DragElementEnum.WIDGET && currentDragApp?.folderId === folder.id
  }

  public setDropTargetFolder(folder: FolderInterface | null): void {
    this._dropTargetFolder.set(folder);
  }

}
